/**
 * Careers — admin (/naytive-careers)
 *
 * Two tabs:
 *   Jobs         — post / edit / open-close / delete job listings
 *   Applications — every submission; filter by job + status, search by name,
 *                  change status, add notes, view/download résumé.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { naytiveAPI } from '../services/api';

const APP_STATUSES = ['new', 'reviewing', 'shortlisted', 'interview', 'rejected', 'hired'];
const STATUS_COLORS = {
  new: { bg: '#FEF3C7', fg: '#92400E' },
  reviewing: { bg: '#DBEAFE', fg: '#1D4ED8' },
  shortlisted: { bg: '#E0E7FF', fg: '#4338CA' },
  interview: { bg: '#EDE9FE', fg: '#5B21B6' },
  rejected: { bg: '#FEE2E2', fg: '#991B1B' },
  hired: { bg: '#D1FAE5', fg: '#065F46' },
};
const emptyJob = { id: null, title: '', slug: '', department: '', location: '', employmentType: 'Full-time', descriptionHtml: '', status: 'open' };

export default function NaytiveCareers() {
  const [tab, setTab] = useState('jobs');
  const [toast, setToast] = useState(null);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2400); };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' }}>Careers</h1>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
            Post jobs to naytive.com/careers and manage applications.
          </div>
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', margin: '18px 0 20px' }}>
            {[['jobs', 'Jobs'], ['applications', 'Applications']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding: '9px 16px', border: 'none', background: 'none', fontSize: 13.5, fontWeight: 500,
                         color: tab === k ? '#111827' : '#6B7280', cursor: 'pointer',
                         borderBottom: tab === k ? '2px solid #2540D9' : '2px solid transparent', marginBottom: -1 }}>
                {l}
              </button>
            ))}
          </div>
          {tab === 'jobs' ? <JobsTab flash={flash} /> : <ApplicationsTab flash={flash} />}
        </div>
      </main>
      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
}

// ─────────────────────────────── Jobs tab ───────────────────────────────
function JobsTab({ flash }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // job draft or null
  const [saving, setSaving] = useState(false);
  const edRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await naytiveAPI.listJobs(); setJobs(r.data?.items || []); }
    catch { setJobs([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...emptyJob }); setTimeout(() => edRef.current?.setHtml(''), 0); };
  const openEdit = async (id) => {
    try {
      const r = await naytiveAPI.getJob(id); const it = r.data?.item;
      const d = { id: it.id, title: it.title || '', slug: it.slug || '', department: it.department || '',
        location: it.location || '', employmentType: it.employment_type || 'Full-time',
        descriptionHtml: it.description_html || '', status: it.status || 'open' };
      setEditing(d); setTimeout(() => edRef.current?.setHtml(d.descriptionHtml), 0);
    } catch { flash('Failed to load job'); }
  };
  const save = async () => {
    if (!editing.title.trim()) { flash('Title required'); return; }
    setSaving(true);
    const body = { ...editing, descriptionHtml: edRef.current?.getHtml() ?? editing.descriptionHtml };
    try {
      if (editing.id) await naytiveAPI.updateJob(editing.id, body);
      else await naytiveAPI.createJob(body);
      flash('Saved'); setEditing(null); await load();
    } catch (e) { flash(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const toggleStatus = async (j) => {
    try { await naytiveAPI.updateJob(j.id, { status: j.status === 'open' ? 'closed' : 'open' }); await load(); }
    catch { flash('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this job? Applications stay but lose their link.')) return;
    try { await naytiveAPI.deleteJob(id); await load(); flash('Deleted'); } catch { flash('Delete failed'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button style={btnPrimary} onClick={openNew}>New job</button>
      </div>
      {loading ? <div style={empty}>Loading…</div>
        : jobs.length === 0 ? <div style={empty}>No jobs yet. Post one — it appears on naytive.com/careers.</div>
        : (
          <div style={card}>
            <table style={table}><thead><tr style={thead}>
              <th style={th}>Title</th><th style={th}>Dept</th><th style={th}>Location</th>
              <th style={th}>Type</th><th style={th}>Status</th><th style={th}>Applications</th><th style={th}></th>
            </tr></thead><tbody>
              {jobs.map((j) => (
                <tr key={j.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={td}><div style={{ fontWeight: 600, color: '#111827' }}>{j.title}</div><div style={{ fontSize: 12, color: '#9CA3AF' }}>/{j.slug}</div></td>
                  <td style={td}>{j.department || '—'}</td>
                  <td style={td}>{j.location || '—'}</td>
                  <td style={td}>{j.employment_type || '—'}</td>
                  <td style={td}><span style={{ ...pill, ...(j.status === 'open' ? pillOpen : pillClosed) }}>{j.status}</span></td>
                  <td style={td}>{j.application_count || 0}{j.new_count > 0 && <span style={{ marginLeft: 6, ...pill, ...pillNew }}>{j.new_count} new</span>}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={mini} onClick={() => openEdit(j.id)}>Edit</button>
                    <button style={{ ...mini, marginLeft: 6 }} onClick={() => toggleStatus(j)}>{j.status === 'open' ? 'Close' : 'Reopen'}</button>
                    <button style={{ ...mini, color: '#B91C1C', marginLeft: 6 }} onClick={() => del(j.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}

      {editing && (
        <div style={overlay} onClick={() => !saving && setEditing(null)}>
          <div style={{ ...modal, width: 'min(720px,100%)' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>{editing.id ? 'Edit job' : 'New job'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Title" full><input style={inp} value={editing.title} onChange={(e) => setEditing((d) => ({ ...d, title: e.target.value }))} /></Field>
              <Field label="Department"><input style={inp} value={editing.department} onChange={(e) => setEditing((d) => ({ ...d, department: e.target.value }))} placeholder="Engineering" /></Field>
              <Field label="Location"><input style={inp} value={editing.location} onChange={(e) => setEditing((d) => ({ ...d, location: e.target.value }))} placeholder="Remote / Bangalore" /></Field>
              <Field label="Employment type"><select style={inp} value={editing.employmentType} onChange={(e) => setEditing((d) => ({ ...d, employmentType: e.target.value }))}>
                {['Full-time', 'Part-time', 'Contract', 'Internship'].map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Status"><select style={inp} value={editing.status} onChange={(e) => setEditing((d) => ({ ...d, status: e.target.value }))}>
                {['open', 'closed', 'draft'].map((t) => <option key={t}>{t}</option>)}</select></Field>
            </div>
            <div style={{ ...lbl, marginTop: 10 }}>Description</div>
            <RichText ref={edRef} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button style={btnGhost} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ────────────────────────── Applications tab ──────────────────────────
function ApplicationsTab({ flash }) {
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobFilter, setJobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (jobFilter) params.jobId = jobFilter;
      if (statusFilter) params.status = statusFilter;
      if (query) params.q = query;
      const r = await naytiveAPI.listApplications(params);
      setApps(r.data?.items || []);
    } catch { setApps([]); } finally { setLoading(false); }
  }, [jobFilter, statusFilter, query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { naytiveAPI.listJobs().then((r) => setJobs(r.data?.items || [])).catch(() => {}); }, []);

  const openDetail = async (id) => {
    try { const r = await naytiveAPI.getApplication(id); setDetail(r.data?.item || null); setNote(''); }
    catch { flash('Failed to load'); }
  };
  const setStatus = async (id, status) => {
    try { await naytiveAPI.updateApplication(id, { status }); await load(); if (detail?.id === id) setDetail((d) => ({ ...d, status })); }
    catch { flash('Failed'); }
  };
  const addNote = async () => {
    if (!note.trim() || !detail) return;
    try { await naytiveAPI.updateApplication(detail.id, { appendNote: note }); const r = await naytiveAPI.getApplication(detail.id); setDetail(r.data?.item); setNote(''); }
    catch { flash('Note failed'); }
  };
  const viewResume = async (id, dl) => {
    try {
      const r = await naytiveAPI.getApplicationResume(id, dl);
      const url = URL.createObjectURL(r.data);
      if (dl) { const a = document.createElement('a'); a.href = url; a.download = 'resume'; document.body.appendChild(a); a.click(); a.remove(); }
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { flash('No résumé / failed'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...inp, width: 'auto' }} value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
        <select style={{ ...inp, width: 'auto' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }} style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <input style={{ ...inp, width: 200 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email…" />
          <button type="submit" style={btnGhost}>Search</button>
        </form>
      </div>
      {loading ? <div style={empty}>Loading…</div>
        : apps.length === 0 ? <div style={empty}>No applications{jobFilter || statusFilter || query ? ' match these filters' : ' yet'}.</div>
        : (
          <div style={card}>
            <table style={table}><thead><tr style={thead}>
              <th style={th}>Applicant</th><th style={th}>Job</th><th style={th}>Status</th><th style={th}>Résumé</th><th style={th}>Applied</th>
            </tr></thead><tbody>
              {apps.map((a) => (
                <tr key={a.id} style={{ borderTop: '1px solid #F3F4F6', cursor: 'pointer' }} onClick={() => openDetail(a.id)}>
                  <td style={td}><div style={{ fontWeight: 600, color: '#111827' }}>{a.name}</div><div style={{ fontSize: 12, color: '#6B7280' }}>{a.email}</div></td>
                  <td style={td}>{a.job_title || '—'}</td>
                  <td style={td}><span style={{ ...pill, ...(STATUS_COLORS[a.status] || {}) }}>{a.status}</span></td>
                  <td style={td}>{a.has_resume ? '📄' : '—'}</td>
                  <td style={td}>{fmt(a.created_at)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}

      {detail && (
        <div style={overlay} onClick={() => setDetail(null)}>
          <div style={{ ...modal, width: 'min(560px,100%)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{detail.name}</h2>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{detail.email}{detail.phone ? ` · ${detail.phone}` : ''}</div>
                <div style={{ fontSize: 12.5, color: '#9CA3AF', marginTop: 2 }}>Applied for <b style={{ color: '#374151' }}>{detail.job_title}</b> · {fmt(detail.created_at)}</div>
              </div>
              <span style={{ ...pill, ...(STATUS_COLORS[detail.status] || {}) }}>{detail.status}</span>
            </div>

            <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' }}>
              {detail.has_resume && <>
                <button style={btnGhost} onClick={() => viewResume(detail.id, false)}>View résumé</button>
                <button style={btnGhost} onClick={() => viewResume(detail.id, true)}>Download</button>
              </>}
              {detail.linkedin && <a style={{ ...btnGhost, textDecoration: 'none' }} href={detail.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>}
              <a style={{ ...btnGhost, textDecoration: 'none' }} href={`mailto:${detail.email}`}>Email</a>
            </div>

            {detail.cover_note && (
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Cover note</div>
                <div style={{ fontSize: 13, color: '#374151', background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: 8, padding: 10, whiteSpace: 'pre-wrap' }}>{detail.cover_note}</div>
              </div>
            )}

            <div style={lbl}>Status</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
              {APP_STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(detail.id, s)}
                  style={{ ...mini, ...(detail.status === s ? { background: (STATUS_COLORS[s] || {}).bg, color: (STATUS_COLORS[s] || {}).fg, borderColor: 'transparent' } : {}), textTransform: 'capitalize' }}>{s}</button>
              ))}
            </div>

            <div style={lbl}>Notes</div>
            {detail.notes && <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12.5, color: '#4B5563', background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: 8, padding: 10, margin: '0 0 8px' }}>{detail.notes}</pre>}
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={inp} value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} placeholder="Add a note…" />
              <button style={btnPrimary} onClick={addNote} disabled={!note.trim()}>Add</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button style={btnGhost} onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── shared bits ──
const RichText = forwardRef((_props, ref) => {
  const elRef = useRef(null);
  useImperativeHandle(ref, () => ({
    setHtml: (h) => { if (elRef.current) elRef.current.innerHTML = h || ''; },
    getHtml: () => elRef.current?.innerHTML || '',
  }));
  const cmd = (c, v = null) => { elRef.current?.focus(); document.execCommand(c, false, v); };
  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 2, padding: '6px 8px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', flexWrap: 'wrap' }}>
        <button type="button" style={tb} onClick={() => cmd('formatBlock', 'H3')}>H3</button>
        <button type="button" style={{ ...tb, fontWeight: 700 }} onClick={() => cmd('bold')}>B</button>
        <button type="button" style={{ ...tb, fontStyle: 'italic' }} onClick={() => cmd('italic')}>I</button>
        <button type="button" style={tb} onClick={() => cmd('insertUnorderedList')}>• List</button>
        <button type="button" style={tb} onClick={() => cmd('insertOrderedList')}>1. List</button>
        <button type="button" style={tb} onClick={() => { const u = window.prompt('Link URL'); if (u) cmd('createLink', u); }}>🔗</button>
      </div>
      <div ref={elRef} contentEditable suppressContentEditableWarning
        style={{ minHeight: 200, maxHeight: 340, overflowY: 'auto', padding: '12px 14px', outline: 'none', fontSize: 14, lineHeight: 1.6, color: '#1F2937' }} />
    </div>
  );
});

const Field = ({ label, children, full }) => (
  <label style={{ display: 'block', gridColumn: full ? '1 / -1' : 'auto' }}><span style={lbl}>{label}</span>{children}</label>
);
function fmt(v) { if (!v) return '—'; const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }

const btnPrimary = { padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2540D9', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const card = { border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thead = { background: '#F9FAFB', textAlign: 'left', color: '#6B7280' };
const th = { padding: '11px 14px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' };
const td = { padding: '12px 14px', color: '#374151', verticalAlign: 'top' };
const pill = { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize' };
const pillOpen = { background: '#D1FAE5', color: '#065F46' };
const pillClosed = { background: '#F3F4F6', color: '#6B7280' };
const pillNew = { background: '#FEF3C7', color: '#92400E' };
const mini = { fontSize: 12, padding: '4px 9px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' };
const empty = { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 };
const modal = { background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', maxHeight: '88vh', overflowY: 'auto' };
const lbl = { display: 'block', fontSize: 11.5, fontWeight: 500, color: '#6B7280', margin: '0 0 4px' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 11px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const tb = { padding: '3px 8px', border: '1px solid transparent', borderRadius: 6, background: 'transparent', color: '#374151', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' };
const toastStyle = { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 };
